import type {
  Advies,
  Contactmoment,
  Afkoppelbesluit,
  Eigendom,
  Foto,
  Maatregelenbesluit,
  RealisatieStatus,
  Registratie,
  SubsidieStatus,
  TuinAdvies,
  UitkomstBezoek,
} from '@/types/domain';

/**
 * Vertaling tussen het domeinmodel en de kolommen van de tabel `registraties`.
 * De kolomnamen hieronder komen exact overeen met supabase/schema.sql.
 */

export interface RegistratieRij {
  id: string;
  bag_id: string | null;
  straat: string;
  huisnummer: string;
  huisletter: string | null;
  toevoeging: string | null;
  postcode: string;
  plaats: string;
  latitude: number | null;
  longitude: number | null;
  eigendom: string;

  datum_bezoek: string | null;
  coach: string;
  uitkomst_bezoek: string;
  uitkomst_toelichting: string;

  bewoner_naam: string;
  bewoner_telefoon: string;
  bewoner_email: string;
  toestemming_avg: boolean;
  bewoner_toelichting: string;

  voortuin_aanwezig: boolean;
  voortuin_advies: string[];
  voortuin_berging_m3: number | string | null;
  voortuin_toelichting: string;

  achtertuin_aanwezig: boolean;
  achtertuin_advies: string[];
  achtertuin_berging_m3: number | string | null;
  achtertuin_toelichting: string;

  afkoppelbesluit: string;
  maatregelen_besluit: string | null;
  subsidie_status: string;
  realisatie_status: string;
  datum_vervolg: string | null;
  algemene_toelichting: string;

  contactmomenten?: Contactmoment[];
  fotos: Foto[];

  aangemaakt_op?: string;
  gewijzigd_op?: string;
  versie?: number;
}

/** Lege datums moeten als NULL naar Postgres, niet als lege string. */
function datum(waarde: string): string | null {
  return waarde ? waarde : null;
}

/** Postgres `numeric` komt soms als string terug. */
function getal(waarde: number | string | null | undefined): number | null {
  if (waarde === null || waarde === undefined || waarde === '') return null;
  const n = Number(waarde);
  return Number.isFinite(n) ? n : null;
}

function tekst(waarde: string | null | undefined): string {
  return waarde ?? '';
}

/* ------------------------------------------------------------------ */
/* Domein -> database                                                  */
/* ------------------------------------------------------------------ */

export function naarRij(registratie: Registratie): RegistratieRij {
  // Zonder AVG-toestemming gaan er geen persoonsgegevens naar de database.
  // Dit staat hier, niet alleen in de UI, zodat het ook geldt voor een
  // registratie die vanuit de offline-wachtrij alsnog wordt verstuurd.
  const magPersoonsgegevens = registratie.bewoner.toestemmingPersoonsgegevens;

  return {
    id: registratie.id,
    bag_id: registratie.bagId || null,
    straat: registratie.adres.straat,
    huisnummer: registratie.adres.huisnummer,
    huisletter: registratie.adres.huisletter ?? null,
    toevoeging: registratie.adres.toevoeging ?? null,
    postcode: registratie.adres.postcode,
    plaats: registratie.adres.plaats,
    latitude: registratie.latitude,
    longitude: registratie.longitude,
    eigendom: registratie.eigendom,

    datum_bezoek: datum(registratie.datumBezoek),
    coach: registratie.coach,
    uitkomst_bezoek: registratie.uitkomstBezoek,
    uitkomst_toelichting: registratie.uitkomstToelichting,

    bewoner_naam: magPersoonsgegevens ? registratie.bewoner.naam : '',
    bewoner_telefoon: magPersoonsgegevens ? registratie.bewoner.telefoon : '',
    bewoner_email: magPersoonsgegevens ? registratie.bewoner.email : '',
    toestemming_avg: magPersoonsgegevens,
    bewoner_toelichting: registratie.bewoner.toelichting,

    voortuin_aanwezig: registratie.voortuin.aanwezig,
    voortuin_advies: registratie.voortuin.advies,
    voortuin_berging_m3: registratie.voortuin.bergingM3,
    voortuin_toelichting: registratie.voortuin.toelichting,

    achtertuin_aanwezig: registratie.achtertuin.aanwezig,
    achtertuin_advies: registratie.achtertuin.advies,
    achtertuin_berging_m3: registratie.achtertuin.bergingM3,
    achtertuin_toelichting: registratie.achtertuin.toelichting,

    afkoppelbesluit: registratie.afkoppelbesluit,
    maatregelen_besluit: registratie.maatregelenbesluit,
    subsidie_status: registratie.subsidieStatus,
    realisatie_status: registratie.realisatieStatus,
    datum_vervolg: datum(registratie.datumVervolg),
    algemene_toelichting: registratie.algemeneToelichting,

    // Alleen geuploade foto's; lokale data-urls horen niet in de database.
    contactmomenten: registratie.contactmomenten,
    fotos: registratie.fotos
      .filter((foto) => !foto.lokaal)
      .map(({ dataUrl: _dataUrl, ...rest }) => rest),
  };
}

/* ------------------------------------------------------------------ */
/* Database -> domein                                                  */
/* ------------------------------------------------------------------ */

function tuin(
  aanwezig: boolean,
  advies: string[] | null,
  berging: number | string | null,
  toelichting: string
): TuinAdvies {
  return {
    aanwezig,
    advies: (advies ?? []) as Advies[],
    bergingM3: getal(berging),
    toelichting: tekst(toelichting),
  };
}

export function vanRij(rij: RegistratieRij): Registratie {
  return {
    id: rij.id,
    bagId: tekst(rij.bag_id),
    adres: {
      straat: tekst(rij.straat),
      huisnummer: tekst(rij.huisnummer),
      huisletter: rij.huisletter || undefined,
      toevoeging: rij.toevoeging || undefined,
      postcode: tekst(rij.postcode),
      plaats: tekst(rij.plaats),
    },
    latitude: getal(rij.latitude) ?? 0,
    longitude: getal(rij.longitude) ?? 0,
    eigendom: tekst(rij.eigendom) as Eigendom | '',

    datumBezoek: tekst(rij.datum_bezoek).slice(0, 10),
    coach: tekst(rij.coach),
    uitkomstBezoek: tekst(rij.uitkomst_bezoek) as UitkomstBezoek | '',
    uitkomstToelichting: tekst(rij.uitkomst_toelichting),

    bewoner: {
      naam: tekst(rij.bewoner_naam),
      telefoon: tekst(rij.bewoner_telefoon),
      email: tekst(rij.bewoner_email),
      toestemmingPersoonsgegevens: Boolean(rij.toestemming_avg),
      toelichting: tekst(rij.bewoner_toelichting),
    },

    voortuin: tuin(
      rij.voortuin_aanwezig,
      rij.voortuin_advies,
      rij.voortuin_berging_m3,
      rij.voortuin_toelichting
    ),
    achtertuin: tuin(
      rij.achtertuin_aanwezig,
      rij.achtertuin_advies,
      rij.achtertuin_berging_m3,
      rij.achtertuin_toelichting
    ),

    afkoppelbesluit: tekst(rij.afkoppelbesluit) as Afkoppelbesluit | '',
    maatregelenbesluit: tekst(rij.maatregelen_besluit) as Maatregelenbesluit | '',
    subsidieStatus: tekst(rij.subsidie_status) as SubsidieStatus | '',
    realisatieStatus: tekst(rij.realisatie_status) as RealisatieStatus | '',
    datumVervolg: tekst(rij.datum_vervolg).slice(0, 10),
    algemeneToelichting: tekst(rij.algemene_toelichting),

    // Rijen van vóór de invoering van contactmomenten hebben alleen de
    // losse bezoekvelden. Daar maken we het eerste contactmoment van, zodat
    // ze meteen meedoen zonder dat de database eerst bijgewerkt hoeft.
    contactmomenten:
      Array.isArray(rij.contactmomenten) && rij.contactmomenten.length > 0
        ? rij.contactmomenten
        : [
            {
              id: `oud_${rij.id}`,
              datum: tekst(rij.datum_bezoek).slice(0, 10),
              soort: 'Bezoek aan de deur' as const,
              coach: tekst(rij.coach),
              uitkomst: tekst(rij.uitkomst_bezoek) as UitkomstBezoek | '',
              toelichting: tekst(rij.uitkomst_toelichting),
            },
          ],

    fotos: Array.isArray(rij.fotos) ? rij.fotos : [],

    aangemaaktOp: tekst(rij.aangemaakt_op) || new Date().toISOString(),
    gewijzigdOp: tekst(rij.gewijzigd_op) || new Date().toISOString(),
    versie: Number(rij.versie) || 1,
    syncState: 'synced',
  };
}
